import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChartBar, Boat, Clock, TrendUp, Users, Wrench, Warning } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend, LineChart, Line, ReferenceLine } from "recharts";
import { fmtDuration } from "../lib/duration";

const CATEGORY_COLORS = {
    equipment: "#E3000F",
    vessel: "#0B1B3D",
    external: "#0EA5E9",
    operations: "#F59E0B",
    documentation: "#7C2D12",
    other: "#64748B",
};
const CATEGORY_LABELS = {
    equipment: "Equipment",
    vessel: "Vessel-side",
    external: "External",
    operations: "Operations",
    documentation: "Documentation",
    other: "Other",
};

/**
 * RBVesselAnalytics — analytics scoped to the RB Vessel Performance branch.
 * Renders inside the branch (no Header). Used in the RB Vessel "Analytics" view.
 */
export default function RBVesselAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/analytics/summary");
                setData(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return <div className="text-slate-500" data-testid="rb-analytics-loading">Loading vessel analytics…</div>;
    }
    if (!data) return null;

    return (
        <div data-testid="rb-vessel-analytics">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                <Kpi icon={<Boat size={18} />} label="Vessels Tracked" value={data.totals.vessels_total} sub={`${data.totals.vessels_active} active`} />
                <Kpi icon={<ChartBar size={18} />} label="Reports Submitted" value={data.totals.reports_submitted} />
                <Kpi icon={<TrendUp size={18} />} label="Avg Flow Rate" value={`${Math.round(data.totals.overall_avg_rate_tph)} t/h`} sub="weighted avg" />
                <Kpi icon={<Clock size={18} />} label="Total Delay" value={formatMins(data.totals.total_delay_minutes)} sub={`${(data.totals.total_tons_loaded || 0).toLocaleString()} tons loaded`} />
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div className="g-card">
                    <div className="g-card-header-red">
                        <Clock size={18} />
                        <div className="font-display font-bold uppercase tracking-wide">Top Delay Reasons</div>
                    </div>
                    {data.top_delay_reasons.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-400" data-testid="no-delay-reasons">No delays recorded.</div>
                    ) : (
                        <div className="p-4" style={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.top_delay_reasons} layout="vertical" margin={{ left: 10 }}>
                                    <CartesianGrid stroke="#EEF1F5" />
                                    <XAxis type="number" stroke="#64748B" fontSize={11} />
                                    <YAxis dataKey="reason" type="category" stroke="#64748B" fontSize={11} width={160} />
                                    <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1" }} />
                                    <Bar dataKey="total_minutes" fill="#E3000F" name="Minutes" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
                <div className="g-card">
                    <div className="g-card-header-dark">
                        <Users size={18} />
                        <div className="font-display font-bold uppercase tracking-wide">Output by Client</div>
                    </div>
                    {data.by_client.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-400" data-testid="no-clients">No completed vessels yet.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="g-table" data-testid="client-table">
                                <thead><tr><th>Client</th><th className="text-right pr-4">Vessels</th><th className="text-right pr-4">Tons delivered</th></tr></thead>
                                <tbody>
                                    {data.by_client.map((c) => (
                                        <tr key={c.client_name}>
                                            <td className="font-semibold text-[var(--grindrod-navy)]">{c.client_name}</td>
                                            <td className="tabular text-right pr-4">{c.vessels}</td>
                                            <td className="tabular text-right pr-4">{Math.round(c.tons).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-5" data-testid="root-cause-block">
                <div className="g-card">
                    <div className="g-card-header-dark">
                        <Warning size={18} weight="fill" />
                        <div className="font-display font-bold uppercase tracking-wide">Delay Categories — Root cause split</div>
                    </div>
                    {(!data.delay_categories || data.delay_categories.length === 0) ? (
                        <div className="p-6 text-center text-sm text-slate-400" data-testid="no-delay-categories">No delays captured.</div>
                    ) : (
                        <div className="p-4 grid grid-cols-2 gap-4 items-center">
                            <div style={{ height: 220 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={data.delay_categories} dataKey="total_minutes" nameKey="category" innerRadius={45} outerRadius={85} paddingAngle={2}>
                                            {data.delay_categories.map((d) => (
                                                <Cell key={d.category} fill={CATEGORY_COLORS[d.category] || "#64748B"} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(v, n, p) => [`${fmtDuration(v)} · ~${Math.round(p.payload.lost_tonnage)} t lost`, CATEGORY_LABELS[p.payload.category] || p.payload.category]} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="space-y-2 text-xs">
                                {data.delay_categories.map((c) => (
                                    <div key={c.category} className="flex items-center gap-2" data-testid={`cat-${c.category}`}>
                                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CATEGORY_COLORS[c.category] || "#64748B" }} />
                                        <span className="capitalize font-semibold text-[var(--grindrod-navy)] flex-1">{CATEGORY_LABELS[c.category] || c.category}</span>
                                        <span className="tabular text-slate-500">{fmtDuration(c.total_minutes)}</span>
                                        <span className="tabular text-[var(--grindrod-red)] font-semibold">~{Math.round(c.lost_tonnage).toLocaleString()} t</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="g-card">
                    <div className="g-card-header-dark">
                        <Wrench size={18} weight="fill" />
                        <div className="font-display font-bold uppercase tracking-wide">MTBF · Mean Time Between Failures</div>
                    </div>
                    {(!data.mtbf || data.mtbf.length === 0) ? (
                        <div className="p-6 text-center text-sm text-slate-400" data-testid="no-mtbf">No equipment-tagged delays yet.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="g-table" data-testid="mtbf-table">
                                <thead><tr><th>Equipment</th><th className="text-right pr-3">Failures</th><th className="text-right pr-3">Total down</th><th className="text-right pr-3">MTBF (h)</th></tr></thead>
                                <tbody>
                                    {data.mtbf.map((m) => (
                                        <tr key={m.equipment} data-testid={`mtbf-${m.equipment}`}>
                                            <td className="font-semibold text-[var(--grindrod-navy)]">{m.equipment}</td>
                                            <td className="tabular text-right pr-3">{m.failures}</td>
                                            <td className="tabular text-right pr-3">{formatMins(m.total_minutes)}</td>
                                            <td className="tabular text-right pr-3 font-bold">{m.mtbf_hours ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="g-card mb-5" data-testid="rate-trend-card">
                <div className="g-card-header-dark">
                    <TrendUp size={18} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">True Flow Rate vs Target</div>
                </div>
                {(!data.rate_trend || data.rate_trend.length === 0) ? (
                    <div className="p-6 text-center text-sm text-slate-400" data-testid="no-rate-trend">Need at least one report.</div>
                ) : (
                    <div className="p-4" style={{ height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.rate_trend.map((r, i) => ({ ...r, idx: i, label: new Date(r.window_end).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) }))}>
                                <CartesianGrid stroke="#EEF1F5" />
                                <XAxis dataKey="label" stroke="#64748B" fontSize={10} interval="preserveStartEnd" />
                                <YAxis stroke="#64748B" fontSize={11} domain={[0, 1800]} />
                                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1" }} formatter={(v) => `${Math.round(v)} t/h`} />
                                <ReferenceLine y={data.totals.target_rate_tph || 1250} stroke="#E3000F" strokeDasharray="4 3" label={{ value: `Target ${data.totals.target_rate_tph || 1250} t/h`, position: "right", fill: "#E3000F", fontSize: 10 }} />
                                <Line type="monotone" dataKey="true_flow_rate_tph" stroke="#0B1B3D" strokeWidth={2} dot={{ r: 2 }} name="True flow rate" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            <div className="g-card">
                <div className="g-card-header-dark">
                    <Boat size={18} />
                    <div className="font-display font-bold uppercase tracking-wide">Completed Vessels · Performance</div>
                </div>
                {data.vessel_performance.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400" data-testid="no-vessel-perf">No completed vessels yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="g-table" data-testid="vessel-perf-table">
                            <thead>
                                <tr>
                                    <th>Vessel</th><th>Client</th>
                                    <th className="text-right pr-4">Tonnage</th>
                                    <th className="text-right pr-4">Avg rate</th>
                                    <th className="text-right pr-4">Total delay</th>
                                    <th>Completed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.vessel_performance.map((v) => (
                                    <tr key={v.id}>
                                        <td className="font-semibold text-[var(--grindrod-navy)]">{v.vessel_name}</td>
                                        <td>{v.client_name}</td>
                                        <td className="tabular text-right pr-4">{v.tonnage.toLocaleString()}</td>
                                        <td className="tabular text-right pr-4">{Math.round(v.avg_rate).toLocaleString()} t/h</td>
                                        <td className="tabular text-right pr-4">{formatMins(v.total_delay_minutes)}</td>
                                        <td className="text-xs text-slate-500">{v.completed_at ? new Date(v.completed_at).toLocaleString(undefined, { hour12: false }) : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function Kpi({ icon, label, value, sub }) {
    return (
        <div className="g-card p-4" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center gap-2 text-slate-500">
                {icon}
                <div className="uppercase-label text-[9px]">{label}</div>
            </div>
            <div className="metric-value tabular text-2xl text-[var(--grindrod-navy)] mt-1">{value}</div>
            {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
        </div>
    );
}

function formatMins(m) {
    return fmtDuration(m);
}
